import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";

import {
  useAddGroupUser,
  useGroup,
  useGroupMembers,
  useRemoveGroupUser,
  useUpdateGroupUser,
  useUsersList,
} from "../../api/queries";
import type { GroupPermission, User } from "../../api/types";
import { useDebounced } from "../../lib/useDebounced";
import { Button } from "../../ui/Button";
import { inputClassName } from "../../ui/Field";
import { SettingsPage, SettingsSection } from "../../ui/SettingsSection";
import { Toggle } from "../../ui/Toggle";
import { useToast } from "../../ui/toast-context";

/**
 * Searching `users.list` to find someone to add, rather than listing the whole
 * workspace: the roster can be thousands long and the picker only ever needs the
 * handful that match what was typed.
 */
function AddMember({ groupId, memberIds }: { groupId: string; memberIds: Set<string> }): ReactNode {
  const [search, setSearch] = useState("");
  const debounced = useDebounced(search);
  const results = useUsersList({ query: debounced, limit: 10, offset: 0 }, debounced.trim() !== "");
  const add = useAddGroupUser();
  const toast = useToast();

  const candidates = (results.data?.users ?? []).filter((user) => !memberIds.has(user.id));

  return (
    <div>
      <label className="sr-only" htmlFor="group-user-search">
        Search people to add
      </label>
      <input
        id="group-user-search"
        type="search"
        value={search}
        placeholder="Search people to add"
        onChange={(event) => {
          setSearch(event.target.value);
        }}
        className={inputClassName}
      />

      {debounced.trim() !== "" && (
        <ul
          aria-label="People to add"
          className="mt-2 divide-y divide-line rounded-md border border-line"
        >
          {results.isPending ? (
            <li className="px-3 py-2 text-sm text-muted">Searching…</li>
          ) : candidates.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted">Nobody else matches.</li>
          ) : (
            candidates.map((user) => (
              <li key={user.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="min-w-0 truncate text-sm">
                  {user.name}
                  <span className="ml-2 text-xs text-muted">{user.email ?? ""}</span>
                </span>
                <Button
                  size="sm"
                  aria-label={`Add ${user.name}`}
                  disabled={add.isPending}
                  onClick={() => {
                    add.mutate(
                      { id: groupId, userId: user.id, permission: "member" },
                      {
                        onSuccess: () => {
                          setSearch("");
                          toast.show(`${user.name} added to the group.`);
                        },
                        onError: (error) => {
                          toast.show(error.message, "error");
                        },
                      },
                    );
                  }}
                >
                  Add
                </Button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

export function GroupMembersScreen(): ReactNode {
  const { id = "" } = useParams();
  const group = useGroup(id);
  const members = useGroupMembers(id);
  const updateUser = useUpdateGroupUser();
  const removeUser = useRemoveGroupUser();
  const toast = useToast();

  const users = members.data?.users ?? [];
  const permissionOf = (user: User): GroupPermission =>
    members.data?.memberships.find((membership) => membership.userId === user.id)?.permission ??
    "member";

  const onError = (error: Error): void => {
    toast.show(error.message, "error");
  };

  return (
    <SettingsPage
      title={group.data?.name ?? "Group"}
      description="Who belongs to this group, and who may manage its roster."
      actions={
        <Link to="/settings/groups" className="text-sm text-muted hover:underline">
          All groups
        </Link>
      }
    >
      <SettingsSection title="Add people" description="Anyone in the workspace may be added.">
        <AddMember groupId={id} memberIds={new Set(users.map((user) => user.id))} />
      </SettingsSection>

      <SettingsSection title="Members">
        {members.isPending ? (
          <p role="status" className="text-sm text-muted">
            Loading members…
          </p>
        ) : members.isError ? (
          <p role="alert" className="text-sm text-danger">
            {members.error.message}
          </p>
        ) : users.length === 0 ? (
          <p className="text-sm text-muted">This group has no members yet.</p>
        ) : (
          <ul
            aria-label="Group members"
            className="divide-y divide-line rounded-md border border-line"
          >
            {users.map((user) => (
              <li key={user.id} className="flex items-center gap-4 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{user.name}</p>
                  <p className="truncate text-xs text-muted">{user.email ?? "—"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted">Group admin</span>
                  <Toggle
                    compact
                    label={`Group admin: ${user.name}`}
                    checked={permissionOf(user) === "admin"}
                    onChange={(checked) => {
                      updateUser.mutate(
                        {
                          id,
                          userId: user.id,
                          permission: checked ? "admin" : "member",
                        },
                        { onError },
                      );
                    }}
                  />
                </div>
                <Button
                  size="sm"
                  variant="danger"
                  aria-label={`Remove ${user.name}`}
                  onClick={() => {
                    removeUser.mutate(
                      { id, userId: user.id },
                      {
                        onSuccess: () => {
                          toast.show(`${user.name} removed from the group.`);
                        },
                        onError,
                      },
                    );
                  }}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </SettingsSection>
    </SettingsPage>
  );
}
